/**
 * Agent Feedback — landlord API client (agent-observability BR-AGENT-FB-*, OBS-BR-24).
 *
 * Response types are DERIVED from the boundary contract in `@resida/api-types/agent`
 * (packages/api-types/src/agent/feedback.ts) — the same Zod schemas apps/api registers — so field
 * drift is a typecheck error. Reports are created only by an improvement conversation that
 * escalates a defect; the landlord can list them and withdraw one while it is open.
 *
 * Covers:
 *   GET  /landlord/api/cases/:id/agent-feedback
 *   POST /landlord/api/agent-feedback/:feedbackId/withdraw
 *
 * The landlord views carry no status / resolution / export fields (BR-24); nothing here
 * exposes one.
 */

import type {
  LandlordFeedbackListResponse,
  LandlordFeedbackViewResponse,
} from '@resida/api-types/agent';
import { apiFetch } from '../lib/api';

export type LandlordFeedbackView = LandlordFeedbackViewResponse;

/** React Query key for one case's report list (shared by the header entry, chips and list). */
export function caseFeedbackQueryKey(caseId: string) {
  return ['agent-feedback', caseId] as const;
}

export async function listCaseFeedback(caseId: string): Promise<LandlordFeedbackListResponse> {
  return apiFetch<LandlordFeedbackListResponse>(
    `/landlord/api/cases/${encodeURIComponent(caseId)}/agent-feedback`,
  );
}

export async function withdrawFeedback(feedbackId: string): Promise<void> {
  await apiFetch<unknown>(
    `/landlord/api/agent-feedback/${encodeURIComponent(feedbackId)}/withdraw`,
    { method: 'POST' },
  );
}

/** Live reports keyed by the CaseEvent they point at — drives the "Reported" chip and Open conversation. */
export function reportsByEventId(reports: LandlordFeedbackView[]): Map<string, LandlordFeedbackView> {
  const byEvent = new Map<string, LandlordFeedbackView>();
  for (const r of reports) {
    if (r.anchorKind === 'point' && r.anchorFrom?.kind === 'case_event') byEvent.set(r.anchorFrom.ref, r);
  }
  return byEvent;
}
